/**
 * Editorial homepage sections — the cascade that brings the lower
 * homepage into the same preserved-atmosphere language as the hero +
 * bridge + archive. NEVER reads as a SaaS feature page.
 *
 * Shared vocabulary across all six sections:
 *   • Georgia serif everywhere, italic for body
 *   • Editorial eyebrow → headline → hairline gold divider
 *   • Restrained gold #D4AF5E sparingly
 *   • Warm dark walnut + ivory text family (#FBEED0)
 *   • NO bright cyan/purple gradients, NO emoji feature pills, NO
 *     star ratings, NO pricing-table grids, NO "👉" arrows
 *   • Asymmetric / magazine layouts where possible
 *   • Reduced motion respected
 *
 * Sections exported:
 *   EditorialDemoSection      — "A night unfolding across every screen ✦"
 *   EditorialSocialProof      — handwritten pull-quotes, NOT testimonials
 *   EditorialProductGrid      — atmospheres, not features
 *   EditorialSchoolsSection   — "When the term ends, the night doesn't"
 *   EditorialPackagesSection  — "Choose how the night will be remembered ✦"
 *   EditorialFinalCTA         — "Some nights fade. The best ones keep breathing ✦"
 *   EditorialFooter           — quiet attribution line, italic
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const IVORY = '#FBEED0';
const GOLD = '#D4AF5E';
const GOLD_MUTED = 'rgba(212,170,82,0.78)';
const PAPER_BG = '#0E0805';
const PAPER_BG_ALT = '#120A06';

// ────────────────────────────────────────────────────────────────
// SHARED PRIMITIVES
// ────────────────────────────────────────────────────────────────
const EditorialEyebrow = ({ children }) => (
  <div style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
    color: GOLD_MUTED, marginBottom: 12,
  }}>
    {children}
  </div>
);

const EditorialHeading = ({ children, max = 720 }) => (
  <h2 style={{
    margin: 0,
    fontFamily: SERIF,
    fontSize: 'clamp(28px, 4.2vw, 48px)',
    fontWeight: 500, lineHeight: 1.1,
    letterSpacing: '-0.015em',
    color: IVORY,
    textShadow: '0 0 24px rgba(212,170,82,0.14)',
    maxWidth: max,
  }}>
    {children}
  </h2>
);

const EditorialSub = ({ children, max = 580 }) => (
  <p style={{
    margin: '18px 0 0',
    maxWidth: max,
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 'clamp(15px, 1.4vw, 18px)',
    lineHeight: 1.6,
    color: 'rgba(255,235,200,0.66)',
  }}>
    {children}
  </p>
);

const HairlineDivider = ({ width = 36 }) => (
  <div aria-hidden style={{
    width, height: 1,
    background: 'rgba(212,170,82,0.32)',
    margin: '10px 0 14px',
  }} />
);

const GhostPill = React.forwardRef(({ to, href, onClick, children, testId, style }, ref) => {
  const C = to ? Link : (href ? 'a' : 'button');
  const props = to ? { to } : (href ? { href } : { type: 'button' });
  const [pressed, setPressed] = useState(false);
  const press = () => setPressed(true);
  const release = () => setPressed(false);
  return (
    <C
      ref={ref}
      {...props}
      data-testid={testId}
      data-pressed={pressed ? '1' : '0'}
      onClick={onClick}
      onPointerDown={press}
      onPointerUp={release}
      onPointerLeave={release}
      onPointerCancel={release}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        height: 52, padding: '0 26px', borderRadius: 999,
        background: 'rgba(18,12,6,0.65)',
        border: `1px solid ${pressed ? 'rgba(212,170,82,0.20)' : 'rgba(212,170,82,0.45)'}`,
        backdropFilter: 'blur(12px)',
        color: '#FFE7A8',
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 16, letterSpacing: '0.01em',
        textDecoration: 'none', cursor: 'pointer',
        boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 220ms ease, border-color 220ms ease',
        ...style,
      }}
    >
      {children}
    </C>
  );
});
GhostPill.displayName = 'GhostPill';

const QuietLink = ({ to, onClick, children, testId }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testId}
    style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 14, color: 'rgba(255,235,200,0.68)',
      textDecoration: 'underline',
      textDecorationColor: 'rgba(212,170,82,0.28)',
      textUnderlineOffset: 4,
    }}
  >
    {children}
  </Link>
);

// ────────────────────────────────────────────────────────────────
// 1 · DEMO SECTION — "A night unfolding across every screen ✦"
// ────────────────────────────────────────────────────────────────
export const EditorialDemoSection = ({ onDemoOpen }) => {
  const videoRef = useRef(null);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !hasPlayed) {
        setHasPlayed(true);
        v.muted = false; v.volume = 0.55;
        v.play().then(() => {
          setIsMuted(false);
          setTimeout(() => {
            if (videoRef.current) {
              videoRef.current.muted = true;
              setIsMuted(true);
            }
          }, 3200);
        }).catch(() => {
          v.muted = true; setIsMuted(true);
          v.play().catch(() => {});
        });
      }
    }, { threshold: 0.4 });
    obs.observe(v);
    return () => obs.disconnect();
  }, [hasPlayed]);

  const handleClick = useCallback(() => {
    const v = videoRef.current;
    if (v) { v.muted = false; v.volume = 0.7; setIsMuted(false); }
    onDemoOpen && onDemoOpen();
  }, [onDemoOpen]);

  return (
    <section
      data-testid="homepage-demo-section"
      style={{
        padding: 'clamp(72px, 9vw, 120px) clamp(20px, 5vw, 28px)',
        background: PAPER_BG,
        position: 'relative',
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9 }}
          style={{ marginBottom: 'clamp(28px, 5vw, 48px)' }}
        >
          <EditorialEyebrow>Chapter three ✦</EditorialEyebrow>
          <EditorialHeading max={760}>
            A night unfolding across every screen <span style={{ color: GOLD }}>✦</span>
          </EditorialHeading>
          <EditorialSub>
            What guests see while it's happening — captured, projected, and quietly preserved for after.
          </EditorialSub>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 1.0, ease: [0.22, 0.61, 0.36, 1] }}
          onClick={handleClick}
          data-testid="homepage-demo-video-frame"
          style={{
            position: 'relative',
            padding: 'clamp(14px, 1.8vw, 22px)',
            borderRadius: 18,
            cursor: 'pointer',
            /* WALNUT FRAME — wraps the video like a wooden projection
               casing so it reads as an installed object inside the
               room, not a marketing tile. */
            background:
              'linear-gradient(135deg, #3a2412 0%, #2a190a 45%, #1e1006 100%)',
            boxShadow:
              '0 40px 100px rgba(212,170,82,0.10), 0 24px 48px rgba(0,0,0,0.55), inset 0 1px 0 rgba(212,170,82,0.18), inset 0 -1px 0 rgba(0,0,0,0.5)',
          }}
        >
          {/* Warm projection halo bleeding past the frame edges */}
          <div aria-hidden style={{
            position: 'absolute', inset: -40, borderRadius: 32,
            background:
              'radial-gradient(ellipse at center, rgba(212,170,82,0.16) 0%, rgba(212,170,82,0) 70%)',
            filter: 'blur(8px)',
            pointerEvents: 'none', zIndex: -1,
          }} />
          <div style={{
            position: 'relative', borderRadius: 8, overflow: 'hidden',
            background: '#0c0703',
            boxShadow:
              'inset 0 0 0 1px rgba(212,170,82,0.22), inset 0 18px 48px rgba(0,0,0,0.45)',
          }}>
            <video
              ref={videoRef}
              src="/demo.mp4"
              muted={isMuted}
              playsInline
              loop
              autoPlay
              preload="metadata"
              poster="/memory-wall-wood.jpg"
              style={{
                display: 'block', width: '100%', height: 'auto',
                /* Warm-grade the video itself so the old cold UI no
                   longer breaks the editorial palette. */
                filter:
                  'sepia(0.18) saturate(0.85) brightness(0.94) hue-rotate(-6deg) contrast(1.04)',
              }}
            />
            {/* Amber tungsten wash — multiplies the video with a
                warm overlay; tints highlights toward gold. */}
            <div aria-hidden style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background:
                'linear-gradient(180deg, rgba(60,30,8,0.12) 0%, rgba(60,30,8,0.08) 40%, rgba(20,10,4,0.45) 100%), radial-gradient(ellipse at 50% 35%, rgba(212,170,82,0.12) 0%, rgba(212,170,82,0) 60%)',
            }} />
            {/* Soft warm vignette */}
            <div aria-hidden style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'radial-gradient(ellipse at 50% 50%, transparent 45%, rgba(20,10,4,0.55) 100%)',
            }} />
            {/* Subtle film grain — keeps the projection feeling
                tactile, never clinical. */}
            <div aria-hidden style={{
              position: 'absolute', inset: 0,
              opacity: 0.12, pointerEvents: 'none',
              backgroundImage:
                'url("data:image/svg+xml;utf8,<svg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\' stitchTiles=\'stitch\'/></filter><rect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/></svg>")',
              mixBlendMode: 'overlay',
            }} />
            <div aria-hidden style={{
              position: 'absolute', bottom: 14, left: 14,
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
              color: GOLD_MUTED,
              background: 'rgba(20,12,6,0.65)',
              border: '1px solid rgba(212,170,82,0.32)',
              backdropFilter: 'blur(8px)',
              padding: '6px 12px', borderRadius: 999,
            }}>
              Tap to wander ✦
            </div>
          </div>
          {/* Walnut frame nameplate — quiet attribution caption */}
          <p style={{
            margin: '12px 4px 0', textAlign: 'center',
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 12, letterSpacing: '0.04em',
            color: 'rgba(255,235,210,0.55)',
          }}>
            A demo wall from a real Year 6 night ✦
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────────
// 2 · SOCIAL PROOF — editorial pull-quotes, NOT testimonials
// ────────────────────────────────────────────────────────────────
const PULL_QUOTES = [
  {
    text: "I didn't expect to cry opening the recap email.",
    attr: "A mother · Year 6 leavers",
    pen: '#A85F60',
    testId: 'pullquote-recap-mother',
  },
  {
    text: "It felt like the room was alive.",
    attr: "Bride · April wedding",
    pen: '#D4AF5E',
    testId: 'pullquote-bride',
  },
  {
    text: "Three weeks later, I'm still pulling chapters from the shelf.",
    attr: "A host · Anniversary",
    pen: '#3F807A',
    testId: 'pullquote-host',
  },
];

export const EditorialSocialProof = () => (
  <section
    data-testid="homepage-pullquotes"
    style={{
      padding: 'clamp(72px, 9vw, 120px) clamp(20px, 5vw, 28px)',
      background: PAPER_BG_ALT,
      position: 'relative',
      borderTop: '1px solid rgba(212,170,82,0.06)',
    }}
  >
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9 }}
        style={{ marginBottom: 'clamp(36px, 5vw, 60px)', maxWidth: 720 }}
      >
        <EditorialEyebrow>Things people said afterwards ✦</EditorialEyebrow>
        <EditorialHeading max={620}>
          The first thing they tell us is how the night kept going
        </EditorialHeading>
      </motion.div>

      {/* Three asymmetric pull-quotes — handwritten in mixed pen colours,
          NOT testimonial cards. The eye reads them like a friend's
          letter, not a marketing wall. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.2fr 0.9fr 1.0fr',
        gap: 'clamp(28px, 3vw, 56px)',
        alignItems: 'start',
      }} className="ed-pullquotes-grid">
        {PULL_QUOTES.map((q, i) => (
          <motion.figure
            key={q.testId}
            data-testid={q.testId}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 1.0, delay: i * 0.12, ease: [0.22, 0.61, 0.36, 1] }}
            style={{
              margin: 0,
              transform: i === 1 ? 'translateY(36px)' : (i === 2 ? 'translateY(14px)' : 'none'),
            }}
          >
            <blockquote style={{
              margin: 0, marginBottom: 22,
              fontFamily: 'Caveat, "Bradley Hand", cursive',
              fontSize: 'clamp(26px, 2.6vw, 36px)',
              lineHeight: 1.25,
              color: q.pen,
              letterSpacing: '0.005em',
              textShadow: '0 1px 0 rgba(0,0,0,0.18)',
            }}>
              {'\u201C'}{q.text}{'\u201D'}
            </blockquote>
            <HairlineDivider width={28} />
            <figcaption style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 13, color: 'rgba(255,235,200,0.62)',
              letterSpacing: '0.04em',
            }}>
              {q.attr}
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>

    <style>{`
      @media (max-width: 880px) {
        .ed-pullquotes-grid {
          grid-template-columns: 1fr !important;
          gap: 40px !important;
        }
        .ed-pullquotes-grid figure { transform: none !important; }
      }
    `}</style>
  </section>
);

// ────────────────────────────────────────────────────────────────
// 3 · PRODUCT GRID — atmospheres, not features
// ────────────────────────────────────────────────────────────────
const ATMOSPHERES = [
  {
    eyebrow: 'During the night',
    title: 'Memory Walls ✦',
    body: 'A wood-and-fairy-lights wall where guests pin polaroids with handwritten messages. The room reacts to every new memory.',
    testId: 'atmosphere-memory-wall',
    accent: '#D4AF5E',
  },
  {
    eyebrow: 'Across the room',
    title: 'Rooms that react together ✦',
    body: 'Every screen in the venue shares the same atmosphere. A new memory lands, and the whole room feels it at once.',
    testId: 'atmosphere-shared-room',
    accent: '#C9A86E',
  },
  {
    eyebrow: 'Days after',
    title: 'Keepsakes guests revisit weeks later ✦',
    body: 'Recap letters and Memory Chapters arrive softly in the inbox 48 hours later — composed like pages from a small private book.',
    testId: 'atmosphere-keepsakes',
    accent: '#B8923F',
  },
  {
    eyebrow: 'Long after',
    title: 'Preserved voices, not forgotten clips ✦',
    body: 'Audio guestbooks and video messages become objects on the archive shelf — pulled down years later, still warm.',
    testId: 'atmosphere-voices',
    accent: '#A85F60',
  },
];

export const EditorialProductGrid = () => (
  <section
    data-testid="homepage-atmospheres"
    style={{
      padding: 'clamp(72px, 9vw, 120px) clamp(20px, 5vw, 28px)',
      background: PAPER_BG,
      position: 'relative',
    }}
  >
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9 }}
        style={{ marginBottom: 'clamp(32px, 5vw, 56px)', maxWidth: 760 }}
      >
        <EditorialEyebrow>What gets built into the night ✦</EditorialEyebrow>
        <EditorialHeading>
          Not features. Atmospheres.
        </EditorialHeading>
        <EditorialSub>
          Each of these isn't a tool you switch on — it's a feeling that lives inside the night and the days that follow.
        </EditorialSub>
      </motion.div>

      {/* Asymmetric 2x2 — each card is a wood panel with serif title +
          italic body. NEVER an icon grid. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 'clamp(20px, 2.4vw, 36px)',
      }} className="ed-atmospheres-grid">
        {ATMOSPHERES.map((a, i) => (
          <motion.div
            key={a.testId}
            data-testid={a.testId}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.95, delay: i * 0.1, ease: [0.22, 0.61, 0.36, 1] }}
            style={{
              padding: '34px 32px 36px',
              borderRadius: 14,
              background: `
                radial-gradient(ellipse at 50% 10%, rgba(212,170,82,0.08) 0%, transparent 55%),
                linear-gradient(180deg, #1c1108 0%, #120a06 100%)
              `,
              border: '1px solid rgba(212,170,82,0.18)',
              boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
              transform: i % 3 === 1 ? 'translateY(24px)' : 'none',
              transition: 'transform 320ms ease, box-shadow 480ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 24px 60px rgba(0,0,0,0.5), 0 0 28px 1px rgba(212,170,82,0.16)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.45)';
            }}
          >
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
              color: a.accent, marginBottom: 10,
            }}>
              {a.eyebrow}
            </div>
            <h3 style={{
              margin: 0, marginBottom: 14,
              fontFamily: SERIF, fontSize: 'clamp(20px, 2vw, 26px)',
              fontWeight: 500, color: IVORY,
              letterSpacing: '-0.005em',
            }}>
              {a.title}
            </h3>
            <HairlineDivider width={28} />
            <p style={{
              margin: 0,
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 15, lineHeight: 1.6,
              color: 'rgba(255,235,200,0.72)',
              maxWidth: 480,
            }}>
              {a.body}
            </p>
          </motion.div>
        ))}
      </div>
    </div>

    <style>{`
      @media (max-width: 880px) {
        .ed-atmospheres-grid {
          grid-template-columns: 1fr !important;
        }
        .ed-atmospheres-grid > div { transform: none !important; }
      }
    `}</style>
  </section>
);

// ────────────────────────────────────────────────────────────────
// 4 · SCHOOLS SECTION — "When the term ends, the night doesn't"
// ────────────────────────────────────────────────────────────────
export const EditorialSchoolsSection = () => (
  <section
    data-testid="homepage-schools"
    style={{
      padding: 'clamp(72px, 9vw, 120px) clamp(20px, 5vw, 28px)',
      background: PAPER_BG_ALT,
      position: 'relative',
      borderTop: '1px solid rgba(212,170,82,0.06)',
    }}
  >
    <div style={{
      maxWidth: 1180, margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: '1.1fr 0.9fr',
      gap: 'clamp(36px, 4vw, 80px)',
      alignItems: 'center',
    }} className="ed-schools-grid">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9 }}
      >
        <EditorialEyebrow>For schools ✦</EditorialEyebrow>
        <EditorialHeading max={520}>
          When the term ends, the night doesn't have to
        </EditorialHeading>
        <EditorialSub max={520}>
          Year 6 leavers, sports days, graduations, summer fairs — preserved as the children themselves will remember them. Not folders of photos. A keepsake the family pulls down from the shelf years later.
        </EditorialSub>
        <div style={{ marginTop: 32 }}>
          <GhostPill to="/schools" testId="schools-cta">
            For schools <span style={{ color: GOLD, marginLeft: 4 }}>✦</span>
          </GhostPill>
        </div>
      </motion.div>

      {/* Right side — a small editorial mock of a school's bookshelf */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 1.0, ease: [0.22, 0.61, 0.36, 1] }}
        style={{
          position: 'relative',
          borderRadius: 14,
          padding: '40px 28px 24px',
          background: `
            radial-gradient(ellipse at 50% 10%, rgba(212,170,82,0.08) 0%, transparent 55%),
            linear-gradient(180deg, #1c1108 0%, #0c0604 100%)
          `,
          border: '1px solid rgba(212,170,82,0.18)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          minHeight: 260,
        }}
      >
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: GOLD_MUTED, marginBottom: 18,
          textAlign: 'center',
        }}>
          Class of 2026 ✦ archive
        </div>
        {/* A miniature shelf of spines */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          gap: 6, height: 160,
        }}>
          {[
            { h: 0.88, t: 28, c: 'linear-gradient(180deg, #15161B 0%, #0c1126 100%)', label: 'Sports Day', foil: '#D4AF5E', lean: -1.4 },
            { h: 0.78, t: 22, c: 'linear-gradient(180deg, #4a1b25 0%, #2a0e16 100%)', label: 'Summer Fair', foil: '#E6B855', lean: 1.0 },
            { h: 0.72, t: 16, c: 'linear-gradient(180deg, #F4ECD9 0%, #E2D5B6 100%)', label: '', foil: '#7A1F26', lean: -0.5, isLetter: true },
            { h: 0.92, t: 32, c: 'linear-gradient(180deg, #1f3a2c 0%, #0e1f17 100%)', label: 'Leavers Night', foil: '#C9A86E', lean: 1.8 },
            { h: 0.84, t: 24, c: 'linear-gradient(180deg, #3d2818 0%, #20140b 100%)', label: 'Trip to France', foil: '#E0BB6A', lean: -0.8 },
          ].map((s, i) => (
            <div key={i} style={{
              width: s.t, height: `${s.h * 100}%`,
              background: s.c,
              boxShadow: '0 6px 12px rgba(0,0,0,0.55), inset 0 12px 16px rgba(0,0,0,0.3)',
              transform: `rotate(${s.lean}deg)`,
              transformOrigin: 'bottom center',
              borderRadius: '2px',
              position: 'relative',
              animation: `schoolShelfBreathe ${7 + i * 0.6}s ease-in-out ${i * 0.5}s infinite`,
            }}>
              {!s.isLetter && (
                <>
                  <div style={{ position: 'absolute', top: '12%', left: 0, right: 0, height: 1.4, background: s.foil, opacity: 0.6 }} />
                  <div style={{ position: 'absolute', bottom: '14%', left: 0, right: 0, height: 1.2, background: s.foil, opacity: 0.5 }} />
                  <div style={{
                    position: 'absolute', inset: '20% 0 22% 0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                    fontFamily: SERIF, fontSize: 7.5,
                    color: s.foil, letterSpacing: '0.16em',
                    textTransform: 'uppercase', fontWeight: 500,
                  }}>{s.label}</div>
                </>
              )}
              {s.isLetter && (
                <>
                  <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 4, background: '#B8923F' }} />
                  <div style={{
                    position: 'absolute', top: '58%', left: '50%',
                    width: 7, height: 7,
                    transform: 'translate(-50%,-50%) rotate(-10deg)',
                    background: 'radial-gradient(circle at 35% 30%, #C2434C 0%, #7A1F26 80%)',
                    borderRadius: '50%',
                  }} />
                </>
              )}
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 18, textAlign: 'center',
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, color: 'rgba(255,235,200,0.55)',
        }}>
          Five chapters preserved this year ✦
        </div>
      </motion.div>
    </div>

    <style>{`
      @keyframes schoolShelfBreathe {
        0%, 100% { filter: brightness(1.0); }
        50%      { filter: brightness(1.08); }
      }
      @media (max-width: 880px) {
        .ed-schools-grid {
          grid-template-columns: 1fr !important;
        }
      }
    `}</style>
  </section>
);

// ────────────────────────────────────────────────────────────────
// 5 · PACKAGES — "Choose how the night will be remembered ✦"
//
// Mirrors the canonical editions on /pricing — The Keepsake / The
// Room / The Archive — at the same price points so the homepage
// matches the commerce surface exactly. Every items list opens with
// the four foundation items (Memory Snap · Memory Wall · Recap
// Letters · Archive Shelf) so the reader sees, at a glance, what is
// preserved in every edition. NOT a feature matrix — an editorial
// preservation list (no ticks, no SaaS comparison grid, no badges
// beyond the single "Most chosen" marker on The Room).
// Updated 2026-05-13 to match the new locked package structure —
// every product is named explicitly; inheritance ("Everything in
// The Keepsake ✦, plus —") is stated quietly above tier additions.
// ────────────────────────────────────────────────────────────────

const PACKAGES = [
  {
    eyebrow: 'For intimate preservation',
    title: 'The Keepsake ✦',
    sub: 'For nights that should be quietly preserved.',
    promise: 'A single night, gently kept.',
    price: '\u00A330',
    items: [
      'QR Photo & Video Guest Upload',
      'Memory Snap',
      'Memory Wall',
      'Recap Letters',
      'Archive Shelf',
      'Highlight Reel',
    ],
    cta: 'Purchase The Keepsake ✦',
    to: '/pricing',
    highlighted: false,
    testId: 'package-keepsake',
  },
  {
    eyebrow: 'For shared atmosphere & live reaction',
    title: 'The Room ✦',
    sub: 'When the night should feel shared by everyone in it.',
    promise: 'Everyone leaves something behind.',
    price: '\u00A360',
    badge: 'Most chosen',
    inherits: 'Everything in The Keepsake ✦, plus —',
    items: [
      'Invites & RSVP',
      'Audio / Video Guestbook',
      'Booth OS · venue kiosk',
      'Pocket Booth · guest phones',
      'Mega Missions',
    ],
    cta: 'Purchase The Room ✦',
    to: '/pricing',
    highlighted: true,
    testId: 'package-room',
  },
  {
    eyebrow: 'For the full preserved-night system',
    title: 'The Archive ✦',
    sub: 'For nights that should keep breathing for years.',
    promise: 'You\u2019ll return to it for decades.',
    price: '\u00A399',
    inherits: 'Everything in The Keepsake ✦ and The Room ✦, plus —',
    items: [
      'Memory Guestbook',
      'Spin Film 360',
      'Event Film',
      'Lens Notes ✦',
      'Pro Upgrade',
    ],
    cta: 'Purchase The Archive ✦',
    to: '/pricing',
    highlighted: false,
    testId: 'package-archive',
  },
];


// ────────────────────────────────────────────────────────────────
// 5.5 · COLLECTION — "Everything the night can become ✦"
// ────────────────────────────────────────────────────────────────
// Sits directly above the editions block. Solves the
// "hosts don't understand what the products actually are" problem
// by surfacing every product in three temporal phases (before /
// during / after the night) with one restrained sentence each.
// Editorial, not a SaaS feature grid.
// ────────────────────────────────────────────────────────────────

const COLLECTION = {
  before: {
    eyebrow: 'Before the night ✦',
    line: 'The night is composed before it begins — invitations sent, missions written.',
    products: [
      {
        title: 'Invites & RSVP',
        line: 'Beautiful invitations with guest responses collected before the event begins.',
        emotion: 'Guests feel expected, not summoned.',
        glyph: '✉',
        edition: 'The Room ✦',
        testId: 'collection-invites',
      },
      {
        title: 'Mega Missions',
        line: 'Interactive challenges and memory prompts guests complete throughout the night.',
        emotion: 'The room participates instead of watching.',
        glyph: '✦',
        edition: 'The Room ✦',
        testId: 'collection-missions',
      },
    ],
  },
  during: {
    eyebrow: 'During the night ✦',
    line: 'The room participates — capture quietly, capture together, capture vividly.',
    products: [
      {
        title: 'Booth OS',
        line: 'A real tablet kiosk at the venue — guests step up and write their memory into the night.',
        emotion: 'The physical entrance into the archive.',
        glyph: '◫',
        edition: 'The Room ✦',
        testId: 'collection-booth-os',
      },
      {
        title: 'Pocket Booth',
        line: 'Phone-based booth guests can open anywhere — the kiosk that travels with every guest.',
        emotion: 'A second booth in every pocket, no tablet needed.',
        glyph: '◉',
        edition: 'The Room ✦',
        testId: 'collection-pocket-booth',
      },
      {
        title: 'QR Upload',
        line: 'Guests scan a code and send photos and videos straight from their phones.',
        emotion: 'No app, no friction — just the night arriving in the gallery.',
        glyph: '◳',
        edition: 'The Keepsake ✦',
        testId: 'collection-qr-upload',
      },
      {
        title: 'Memory Snap',
        line: 'Guests take Polaroid-style photos and leave a handwritten message beside them.',
        emotion: 'Words and faces, settling into the same archive.',
        glyph: '◐',
        edition: 'The Keepsake ✦',
        testId: 'collection-memory-snap',
      },
      {
        title: 'Memory Wall',
        line: 'A live shared wall where the room watches new memories appear together.',
        emotion: 'Everyone feels the night being made.',
        glyph: '▦',
        edition: 'The Keepsake ✦',
        testId: 'collection-memory-wall',
      },
      {
        title: 'Audio / Video Guestbook',
        line: 'Guests leave spoken video and audio messages for the host.',
        emotion: 'Voices preserved, not forgotten clips.',
        glyph: '◌',
        edition: 'The Room ✦',
        testId: 'collection-guestbook',
      },
      {
        title: 'Lens Notes ✦',
        line: 'Handwritten video memories — guests record short videos while writing directly onto the night itself.',
        emotion: 'Handwritten video memories from the people who lived the night beside you ✦',
        glyph: '✎',
        edition: 'The Archive ✦',
        testId: 'collection-lens-notes',
      },
      {
        title: 'Spin Film 360',
        line: 'Immersive rotating 360 captures from the dancefloor and event space.',
        emotion: 'The room itself remembered, not just its faces.',
        glyph: '◯',
        edition: 'The Archive ✦',
        testId: 'collection-spin-360',
      },
    ],
  },
  after: {
    eyebrow: 'After the night ✦',
    line: 'Long after the room has emptied, the night keeps breathing.',
    products: [
      {
        title: 'Highlight Reel',
        line: 'A short cinematic recap composed from the night\u2019s best moments.',
        emotion: 'The night, distilled — sent before the day ends.',
        glyph: '▶',
        edition: 'The Keepsake ✦',
        testId: 'collection-highlight',
      },
      {
        title: 'Event Film',
        line: 'A fuller storytelling edit preserving the atmosphere of the entire event.',
        emotion: 'A keepsake the host returns to for years.',
        glyph: '▣',
        edition: 'The Archive ✦',
        testId: 'collection-event-film',
      },
      {
        title: 'Recap Letters',
        line: 'Guests receive a quiet memory-letter from the night, days after it ends.',
        emotion: 'The room reaches out before it fades.',
        glyph: '✉',
        edition: 'The Keepsake ✦',
        testId: 'collection-recap',
      },
      {
        title: 'Archive Shelf',
        line: 'A preserved archive where the night quietly lives on — pulled down years later.',
        emotion: 'Not stored. Kept.',
        glyph: '⎯',
        edition: 'The Keepsake ✦',
        testId: 'collection-archive',
      },
      {
        title: 'Memory Guestbook',
        line: 'A printable keepsake book of memories, photos, and guest messages.',
        emotion: 'A book the family opens at the anniversary.',
        glyph: '◇',
        edition: 'The Archive ✦',
        testId: 'collection-memory-guestbook',
      },
    ],
  },
};

const CollectionPhase = ({ phase }) => (
  <div
    data-testid={`collection-phase-${phase.eyebrow.split(' ')[0].toLowerCase()}`}
    style={{
      marginBottom: 'clamp(56px, 8vw, 96px)',
    }}
  >
    {/* Phase header — eyebrow + restrained editorial line. Lives at
        the top on mobile (stacked), and on the left on wide screens
        (asymmetric magazine layout, not a centered marketing block). */}
    <div className="collection-phase-header" style={{
      maxWidth: 580, marginBottom: 'clamp(20px, 3vw, 32px)',
    }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12, letterSpacing: '0.26em', textTransform: 'uppercase',
        color: GOLD, marginBottom: 10,
      }}>{phase.eyebrow}</div>
      <p style={{
        margin: 0,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(15px, 1.8vw, 17px)',
        lineHeight: 1.6,
        color: 'rgba(255,235,200,0.7)',
      }}>
        {phase.line}
      </p>
    </div>

    {/* Cards row — flex-wrap on wider widths, single-column stacked
        on mobile via .collection-cards CSS. Each card is tactile +
        composed; never a SaaS tile. */}
    <div className="collection-cards" style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
      gap: 14,
    }}>
      {phase.products.map((p) => (
        <article
          key={p.testId}
          data-testid={p.testId}
          className="collection-card"
          style={{
            position: 'relative',
            background:
              'linear-gradient(155deg, rgba(40,22,10,0.78) 0%, rgba(20,11,5,0.85) 100%)',
            border: '1px solid rgba(212,170,82,0.16)',
            borderRadius: 14,
            padding: 'clamp(20px, 3vw, 26px) clamp(20px, 3vw, 26px) clamp(22px, 3vw, 28px)',
            boxShadow:
              '0 14px 36px rgba(0,0,0,0.34), inset 0 1px 0 rgba(212,170,82,0.06)',
            // Tactile press feel — a quiet inward shrink on hover,
            // never a SaaS-style lift. Touch-friendly target.
            transition: 'transform 280ms cubic-bezier(0.16,1,0.3,1), border-color 280ms ease, box-shadow 280ms ease',
            cursor: 'default',
            minHeight: 'clamp(150px, 14vw, 170px)',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          <div aria-hidden style={{
            fontSize: 24, color: GOLD,
            fontWeight: 300,
            lineHeight: 1,
            marginBottom: 4,
          }}>
            {p.glyph}
          </div>
          <h3 style={{
            margin: 0,
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 'clamp(17px, 1.8vw, 19px)',
            letterSpacing: '-0.01em',
            color: IVORY,
          }}>{p.title}</h3>
          <p style={{
            margin: 0,
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontSize: 'clamp(13px, 1.5vw, 14px)',
            lineHeight: 1.55,
            color: 'rgba(255,235,200,0.62)',
          }}>
            {p.line}
          </p>
          {/* Emotional outcome — one short italic line that names the
              feeling the product produces, not the mechanism. Per
              Craig's 2026-05-15 Homepage Clarity brief: every product
              card carries (1) one sentence of fact and (2) one short
              line of feeling. The feeling line is what a visitor
              remembers ten minutes after leaving the homepage. */}
          {p.emotion && (
            <p
              data-testid={`${p.testId}-emotion`}
              style={{
                margin: '6px 0 0',
                fontFamily: SERIF,
                fontStyle: 'italic',
                fontSize: 'clamp(12.5px, 1.4vw, 13.5px)',
                lineHeight: 1.5,
                color: 'rgba(212,170,82,0.78)',
                letterSpacing: '0.005em',
                flex: 1,
              }}
            >
              {p.emotion}
            </p>
          )}
          {/* Edition origin micro-line — lets price-shopping hosts
              instantly map a product to its tier without leaving the
              section. Quieter than the description so it never
              competes with the editorial body. */}
          {p.edition && (
            <div
              data-testid={`${p.testId}-edition`}
              style={{
                marginTop: 4,
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 11,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'rgba(212,170,82,0.6)',
              }}
            >
              Included from {p.edition}
            </div>
          )}
        </article>
      ))}
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────
// "See the booth in motion ✦" — proof-of-life motion insert
// ────────────────────────────────────────────────────────────────
// A restrained 10-second loop captured cleanly from the live booth
// at /booth-os/visual_mock_evt_001 (regenerable via
// `yarn motion:capture`). NOT a promo video, NOT a feature reel.
//
// Per Craig's 2026-05-15 brief — proof of life:
//   "Turn the booth from a screenshot / concept / product card into
//    a believable physical experience. Credibility through atmosphere."
//
// Locks honoured:
//   • Not hero-level. Not fullscreen-first. Lives inside the booth
//     section as a quiet press affordance.
//   • Lightbox is a single dark walnut overlay, no browser chrome,
//     no controls until hovered/tapped.
//   • Muted by default. NO autoplay sound. Silence is safer.
//   • Video lazy-loaded only when the lightbox opens — the
//     homepage shows poster only until press.
//   • Singular. Never extend to multi-video carousels or feature
//     walkthroughs.
//   • Poster = the existing /booth-launcher-real.png (same Rustic
//     Evening surface, one canonical image, zero asset drift).
// ────────────────────────────────────────────────────────────────
const BoothMotionPlayer = () => {
  const [open, setOpen] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const videoRef = useRef(null);
  const closerRef = useRef(null);

  // Lock body scroll and bind Escape while the overlay is open.
  // The lightbox is purposely keyboard-dismissable — a kiosk
  // experience should never feel like a trap.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    // Move focus into the overlay for screen readers + keyboard
    // users. The closer button is the canonical entry point.
    setTimeout(() => { try { closerRef.current?.focus(); } catch {} }, 30);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Pause the loop when the overlay closes — no orphaned media
  // streams playing under a hidden surface.
  useEffect(() => {
    if (!open && videoRef.current) {
      try { videoRef.current.pause(); videoRef.current.currentTime = 0; } catch {}
    }
  }, [open]);

  return (
    <>
      {/* Trigger — italic gold QuietPress beneath the screenshot.
          Never reads as a CTA. Reads as a quiet invitation. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="homepage-booth-motion-trigger"
        style={{
          marginTop: 18,
          alignSelf: 'flex-start',
          background: 'transparent',
          border: 'none',
          padding: '8px 4px 8px 0',
          cursor: 'pointer',
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 14, letterSpacing: '0.01em',
          color: GOLD_MUTED,
          textDecoration: 'underline',
          textDecorationColor: 'rgba(212,170,82,0.32)',
          textUnderlineOffset: 5,
          transition: 'color 220ms ease, text-decoration-color 220ms ease',
        }}
        onPointerEnter={(e) => {
          e.currentTarget.style.color = IVORY;
          e.currentTarget.style.textDecorationColor = 'rgba(212,170,82,0.6)';
        }}
        onPointerLeave={(e) => {
          e.currentTarget.style.color = GOLD_MUTED;
          e.currentTarget.style.textDecorationColor = 'rgba(212,170,82,0.32)';
        }}
      >
        See the booth in motion ✦
      </button>

      {/* Overlay — single dark walnut sheet. Press outside the
          frame to dismiss. No browser-chrome, no YouTube look. */}
      {open && (
        <div
          data-testid="homepage-booth-motion-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 'clamp(16px, 4vw, 48px)',
            background: 'rgba(8, 4, 1, 0.92)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            animation: 'motionOverlayFade 320ms ease-out',
          }}
        >
          <div
            onPointerEnter={() => setShowControls(true)}
            onPointerLeave={() => setShowControls(false)}
            style={{
              position: 'relative',
              width: 'min(960px, 100%)',
              aspectRatio: '16 / 10',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#0E0805',
              border: '1px solid rgba(212,170,82,0.24)',
              boxShadow: '0 40px 120px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,170,82,0.08)',
            }}
          >
            {/* The actual loop. `controls` toggled only on hover
                so the resting state reads as installation, not
                player. `playsInline` for iOS Safari. Muted always.
                MP4 first for Safari, WebM second for Chrome/
                Firefox (browsers pick the first source they can
                decode, and Safari can't decode VP9 in WebM). */}
            <video
              ref={videoRef}
              data-testid="homepage-booth-motion-video"
              autoPlay
              muted
              loop
              playsInline
              controls={showControls}
              preload="metadata"
              poster="/booth-launcher-real.png"
              style={{
                display: 'block',
                width: '100%', height: '100%',
                objectFit: 'cover',
              }}
            >
              <source src="/booth-motion.mp4" type="video/mp4" />
              <source src="/booth-motion.webm" type="video/webm" />
            </video>

            {/* Faint gold hairline frame — keeps the lightbox
                reading as an installation, not a media-player. */}
            <div aria-hidden style={{
              position: 'absolute', inset: 12,
              border: '1px solid rgba(212,170,82,0.10)',
              borderRadius: 4,
              pointerEvents: 'none',
            }} />

            {/* Close — italic, restrained, top-right. Never bright,
                never crossed-out X iconography. Operators close by
                pressing outside or Escape; this is the visible
                hint for everyone else. */}
            <button
              ref={closerRef}
              type="button"
              onClick={() => setOpen(false)}
              data-testid="homepage-booth-motion-close"
              aria-label="Close the booth motion preview"
              style={{
                position: 'absolute', top: 14, right: 16,
                background: 'rgba(20,12,6,0.65)',
                border: '1px solid rgba(212,170,82,0.38)',
                borderRadius: 999,
                padding: '6px 14px',
                color: '#FFE7A8',
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 12.5, letterSpacing: '0.01em',
                cursor: 'pointer',
                backdropFilter: 'blur(6px)',
              }}
            >
              Close ✦
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes motionOverlayFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </>
  );
};

// ────────────────────────────────────────────────────────────────
// THE BOOTH — the physical entrance into EventDrop
// ────────────────────────────────────────────────────────────────
// Per Craig's 2026-05-15 Homepage Clarity brief: the booth is no
// longer "an add-on feature." It is the physical entrance into
// EventDrop — a real tablet kiosk at the venue, atmosphere-styled,
// used at actual weddings, proms, and gatherings. The homepage
// must communicate that visually with a real product screenshot,
// not stock photography.
//
// One real screenshot. One sentence per audience (guest · host ·
// venue). Restrained. Atmospheric. Never SaaS marketing.
// ────────────────────────────────────────────────────────────────
export const EditorialBoothSection = () => (
  <section
    data-testid="homepage-booth"
    style={{
      padding: 'clamp(72px, 9vw, 120px) clamp(20px, 5vw, 28px)',
      background: PAPER_BG,
      position: 'relative',
      overflow: 'hidden',
      borderTop: '1px solid rgba(212,170,82,0.06)',
    }}
  >
    <div
      style={{
        maxWidth: 1180, margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '0.95fr 1.05fr',
        gap: 'clamp(36px, 5vw, 80px)',
        alignItems: 'center',
      }}
      className="ed-booth-grid"
    >
      {/* Left column — screenshot + motion press, stacked. */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.95, ease: [0.22, 0.61, 0.36, 1] }}
          data-testid="homepage-booth-frame"
          style={{
            position: 'relative',
            aspectRatio: '16 / 10',
            borderRadius: 8,
            overflow: 'hidden',
            background: '#0E0805',
            border: '1px solid rgba(212,170,82,0.22)',
            boxShadow:
              '0 36px 80px rgba(0,0,0,0.55), 0 0 32px 1px rgba(212,170,82,0.08), inset 0 1px 0 rgba(212,170,82,0.08)',
          }}
        >
          <img
            src="/booth-launcher-real.png"
            alt="A Booth OS kiosk standing at a venue, showing the four-card launcher"
            data-testid="homepage-booth-screenshot"
            loading="lazy"
            decoding="async"
            style={{
              display: 'block',
              width: '100%', height: '100%',
              objectFit: 'cover',
              // The launcher already lives inside its own walnut
              // atmosphere — no veil, no neon overlay. Let the surface
              // speak for itself.
            }}
          />
          {/* Faint gold hairline frame inside the image — reads as a
              polished installation frame, not a website screenshot. */}
          <div aria-hidden style={{
            position: 'absolute', inset: 10,
            border: '1px solid rgba(212,170,82,0.10)',
            borderRadius: 4,
            pointerEvents: 'none',
          }} />
        </motion.div>

        {/* Proof-of-life motion press — quiet invitation beneath
            the still. Lazy-loads the video file only when opened. */}
        <BoothMotionPlayer />
      </div>

      {/* Right column — the clarity layer. Three short audience
          lines, never feature bullets. Eyebrow → headline → lede
          → three preserved sentences. */}
      <motion.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.95, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
      >
        <EditorialEyebrow>The physical entrance ✦</EditorialEyebrow>
        <EditorialHeading max={520}>
          A real kiosk at the venue.<br />Not a browser tab.
        </EditorialHeading>
        <EditorialSub max={500}>
          Booth OS runs on a tablet at the room. Guests step up, choose a way to remember, and the night quietly fills the archive — even with no signal, even with the WiFi down.
        </EditorialSub>

        <div
          data-testid="homepage-booth-lines"
          style={{
            marginTop: 'clamp(28px, 3.4vw, 36px)',
            display: 'flex', flexDirection: 'column',
            gap: 18,
            maxWidth: 520,
          }}
        >
          {[
            {
              eyebrow: 'For the guest',
              line: 'Step up. Choose to write, photograph, or speak. The kiosk feels like a part of the venue, not a piece of software.',
              testId: 'homepage-booth-line-guest',
            },
            {
              eyebrow: 'For the host',
              line: 'Pair the booth once with a six-digit code. From then on, every memory drops quietly into the archive without anyone watching.',
              testId: 'homepage-booth-line-host',
            },
            {
              eyebrow: 'For the venue',
              line: 'Holds steady without signal. Idle-attract when the room rests. Long-press exit when the night ends. Designed for real wedding nights — not demo days.',
              testId: 'homepage-booth-line-venue',
            },
          ].map((row) => (
            <div key={row.testId} data-testid={row.testId} style={{
              borderLeft: '1px solid rgba(212,170,82,0.28)',
              paddingLeft: 14,
            }}>
              <div style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 10.5, letterSpacing: '0.26em', textTransform: 'uppercase',
                color: GOLD_MUTED, marginBottom: 6,
              }}>
                {row.eyebrow}
              </div>
              <p style={{
                margin: 0,
                fontFamily: SERIF,
                fontStyle: 'italic',
                fontSize: 'clamp(14px, 1.55vw, 15.5px)',
                lineHeight: 1.6,
                color: 'rgba(255,235,200,0.78)',
              }}>
                {row.line}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>

    <style>{`
      @media (max-width: 880px) {
        [data-testid="homepage-booth"] .ed-booth-grid {
          grid-template-columns: 1fr !important;
          gap: 36px !important;
        }
      }
    `}</style>
  </section>
);

// ────────────────────────────────────────────────────────────────
// 5.5 · COLLECTION — section header restored
// ────────────────────────────────────────────────────────────────
export const EditorialCollectionSection = () => (
  <section
    data-testid="homepage-collection"
    style={{
      padding: 'clamp(72px, 9vw, 120px) clamp(20px, 5vw, 28px)',
      background: PAPER_BG_ALT,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* Hairline gold ornament at the top — quiet visual seam from
        the previous section. */}
    <div aria-hidden style={{
      position: 'absolute', top: 0, left: '50%',
      width: 80, height: 1,
      transform: 'translateX(-50%)',
      background: 'linear-gradient(90deg, transparent, rgba(212,170,82,0.45), transparent)',
    }} />

    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      {/* Section header — eyebrow + serif heading + restrained line.
          Editorial; no center-aligned SaaS hero copy. */}
      <header style={{
        maxWidth: 760, marginBottom: 'clamp(48px, 6vw, 72px)',
      }}>
        <EditorialEyebrow>✦ The Collection</EditorialEyebrow>
        <EditorialHeading>
          Everything the night can become <span style={{ color: GOLD }}>✦</span>
        </EditorialHeading>
        <p style={{
          marginTop: 18,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.9vw, 18px)',
          lineHeight: 1.7,
          color: 'rgba(255,235,200,0.7)',
          maxWidth: 620,
        }}>
          Before the room gathers, during the night itself, and long after it has been revisited.
        </p>
      </header>

      <CollectionPhase phase={COLLECTION.before} />
      <CollectionPhase phase={COLLECTION.during} />
      <CollectionPhase phase={COLLECTION.after} />
    </div>

    {/* Local responsive rules — mobile-first stacking, never cramped. */}
    <style>{`
      @media (max-width: 480px) {
        [data-testid="homepage-collection"] .collection-cards {
          grid-template-columns: 1fr !important;
          gap: 12px !important;
        }
        [data-testid="homepage-collection"] .collection-card {
          min-height: 0 !important;
        }
      }
    `}</style>
  </section>
);

export const EditorialPackagesSection = () => (
  <section
    data-testid="homepage-packages"
    style={{
      padding: 'clamp(72px, 9vw, 120px) clamp(20px, 5vw, 28px)',
      background: PAPER_BG,
      position: 'relative',
    }}
  >
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9 }}
        style={{ marginBottom: 'clamp(32px, 5vw, 56px)', maxWidth: 760 }}
      >
        <EditorialEyebrow>Chapter four ✦</EditorialEyebrow>
        <EditorialHeading>
          Choose how the night will be remembered <span style={{ color: GOLD }}>✦</span>
        </EditorialHeading>
        <EditorialSub>
          Three editions. The same restraint runs through each — the only difference is how much of the night you preserve.
        </EditorialSub>
      </motion.div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'clamp(20px, 2.4vw, 36px)',
        alignItems: 'stretch',
      }} className="ed-packages-grid">
        {PACKAGES.map((p, i) => (
          <motion.div
            key={p.testId}
            data-testid={p.testId}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 1.0, delay: i * 0.12, ease: [0.22, 0.61, 0.36, 1] }}
            style={{
              position: 'relative',
              padding: '36px 32px 32px',
              borderRadius: 16,
              background: p.highlighted
                ? `
                  radial-gradient(ellipse at 50% 10%, rgba(212,170,82,0.14) 0%, transparent 55%),
                  linear-gradient(180deg, #261810 0%, #150B07 100%)
                `
                : `
                  radial-gradient(ellipse at 50% 10%, rgba(212,170,82,0.06) 0%, transparent 55%),
                  linear-gradient(180deg, #1c1108 0%, #120a06 100%)
                `,
              border: p.highlighted
                ? '1px solid rgba(212,170,82,0.55)'
                : '1px solid rgba(212,170,82,0.18)',
              boxShadow: p.highlighted
                ? '0 30px 70px rgba(0,0,0,0.55), 0 0 32px 1px rgba(212,170,82,0.18)'
                : '0 20px 50px rgba(0,0,0,0.45)',
              transform: p.highlighted ? 'translateY(-10px)' : 'none',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              transition: 'transform 320ms ease, box-shadow 480ms ease',
            }}
          >
            {/* SUBTLE ARTIFACT MOTIF — each package card carries a
                whisper of the artifact it produces, painted at
                5-8% opacity so it never competes with the copy.
                Per the brand restraint: NEVER decorative — always
                referential. */}
            {p.testId === 'package-keepsake' && (
              <div aria-hidden style={{
                position: 'absolute', top: 18, right: 16,
                width: 96, height: 96, pointerEvents: 'none',
                opacity: 0.08,
              }}>
                {/* Three pinned mini polaroids fanned */}
                {[
                  { rot: -10, tx: 0,  ty: 6  },
                  { rot:   4, tx: 18, ty: -2 },
                  { rot:  -2, tx: 38, ty: 10 },
                ].map((s, idx) => (
                  <div key={idx} style={{
                    position: 'absolute', left: s.tx, top: s.ty,
                    width: 38, height: 46,
                    background: '#FFE7A8', borderRadius: 1.5,
                    transform: `rotate(${s.rot}deg)`,
                    border: '1px solid rgba(212,170,82,0.55)',
                  }}>
                    <div style={{
                      width: '100%', height: 30,
                      background: 'rgba(120,80,30,0.5)',
                    }} />
                  </div>
                ))}
              </div>
            )}
            {p.testId === 'package-room' && (
              <>
                {/* Faint projection glow behind the card content —
                    reads as the room itself catching warm light. */}
                <div aria-hidden style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none',
                  background:
                    'radial-gradient(circle at 50% 30%, rgba(212,170,82,0.16) 0%, rgba(212,170,82,0) 55%)',
                  opacity: 1,
                  zIndex: 0,
                }} />
                <div aria-hidden style={{
                  position: 'absolute', top: 30, left: '50%',
                  transform: 'translateX(-50%)',
                  width: 220, height: 220, borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(212,170,82,0.07) 0%, rgba(212,170,82,0) 70%)',
                  filter: 'blur(20px)', pointerEvents: 'none',
                  zIndex: 0,
                }} />
              </>
            )}
            {p.testId === 'package-archive' && (
              <div aria-hidden style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                height: 64, pointerEvents: 'none', opacity: 0.07,
                display: 'flex', alignItems: 'flex-end',
                paddingLeft: 18, paddingRight: 18, gap: 4,
              }}>
                {/* Bookshelf spines — varied widths/heights, never uniform */}
                {[
                  { w: 11, h: 44, c: '#D4AF5E' },
                  { w: 8,  h: 52, c: '#C49A4C' },
                  { w: 12, h: 38, c: '#A87434' },
                  { w: 9,  h: 50, c: '#E2BD6E' },
                  { w: 7,  h: 42, c: '#B88D44' },
                  { w: 10, h: 56, c: '#D4AF5E' },
                  { w: 8,  h: 36, c: '#9C6A30' },
                  { w: 11, h: 48, c: '#C49A4C' },
                  { w: 9,  h: 44, c: '#E2BD6E' },
                ].map((sp, idx) => (
                  <div key={idx} style={{
                    width: sp.w, height: sp.h,
                    background: sp.c, borderRadius: 1.5,
                  }} />
                ))}
              </div>
            )}
            {/* All inner content lifts above the motif via z-index */}
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1 }}>
            {p.badge && (
              <div data-testid={`${p.testId}-badge`} style={{
                position: 'absolute',
                top: -12, left: '50%', transform: 'translateX(-50%)',
                padding: '5px 14px', borderRadius: 999,
                background: 'rgba(20,12,6,0.85)',
                border: `1px solid ${GOLD}`,
                color: '#FFE7A8',
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
                backdropFilter: 'blur(8px)',
              }}>
                {p.badge}
              </div>
            )}

            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
              color: GOLD_MUTED, marginBottom: 8,
            }}>
              {p.eyebrow}
            </div>
            <h3 style={{
              margin: 0, marginBottom: 6,
              fontFamily: SERIF, fontSize: 'clamp(22px, 2.2vw, 28px)',
              fontWeight: 500, color: IVORY, letterSpacing: '-0.005em',
            }}>
              {p.title}
            </h3>
            <p style={{
              margin: 0, marginBottom: 12,
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 14, color: 'rgba(255,235,200,0.62)', lineHeight: 1.5,
            }}>
              {p.sub}
            </p>
            <HairlineDivider />
            <p data-testid={`${p.testId}-emotion`} style={{
              margin: 0, marginBottom: 18,
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 15, color: p.highlighted ? IVORY : 'rgba(255,235,200,0.86)',
              letterSpacing: '0.005em', lineHeight: 1.5,
            }}>
              "{p.promise}"
            </p>

            <div data-testid={`${p.testId}-price`} style={{
              marginBottom: 22,
              fontFamily: SERIF, fontSize: 30, fontWeight: 500,
              color: IVORY, letterSpacing: '-0.02em',
            }}>
              {p.price}
            </div>

            {p.inherits && (
              <div
                data-testid={`${p.testId}-inherits`}
                style={{
                  marginBottom: 14,
                  fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 13,
                  color: 'rgba(212,170,82,0.85)',
                  letterSpacing: '0.01em',
                  lineHeight: 1.5,
                }}
              >
                {p.inherits}
              </div>
            )}

            <ul
              data-testid={`${p.testId}-items`}
              style={{
                listStyle: 'none', margin: 0, padding: 0, marginBottom: 28,
                display: 'flex', flexWrap: 'wrap', gap: 8,
                flex: 1, alignContent: 'flex-start',
              }}
            >
              {p.items.map((it, j) => (
                <li
                  key={j}
                  data-testid={`${p.testId}-chip-${j}`}
                  style={{
                    // Tactile chip — reads as a small included artifact,
                    // not a checkbox row. Replaces the previous bullet
                    // list per Craig's 2026-05-15 Homepage Clarity brief:
                    // "show package contents visually as tactile cards,
                    // no hidden inclusions, no mental arithmetic."
                    fontFamily: SERIF, fontSize: 13.5,
                    fontStyle: 'italic',
                    lineHeight: 1.3,
                    color: 'rgba(255,235,200,0.92)',
                    padding: '7px 12px 7px 11px',
                    background: p.highlighted
                      ? 'rgba(212,170,82,0.10)'
                      : 'rgba(212,170,82,0.06)',
                    border: '1px solid rgba(212,170,82,0.28)',
                    borderRadius: 6,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    letterSpacing: '0.005em',
                  }}
                >
                  <span aria-hidden style={{
                    color: GOLD, fontSize: 9, lineHeight: 1,
                  }}>✦</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>

            <GhostPill to={p.to} testId={`${p.testId}-cta`}>
              {p.cta} <span style={{ color: GOLD, marginLeft: 4 }}>✦</span>
            </GhostPill>
            </div>
          </motion.div>
        ))}
      </div>
    </div>

    <style>{`
      @media (max-width: 980px) {
        .ed-packages-grid {
          grid-template-columns: 1fr !important;
        }
        .ed-packages-grid > div { transform: none !important; }
      }
    `}</style>
  </section>
);

// ────────────────────────────────────────────────────────────────
// 6 · FINAL CTA — "Some nights fade. The best ones keep breathing ✦"
// ────────────────────────────────────────────────────────────────
export const EditorialFinalCTA = () => (
  <section
    data-testid="homepage-final-cta"
    style={{
      padding: 'clamp(72px, 9vw, 120px) clamp(20px, 5vw, 28px)',
      background: PAPER_BG_ALT,
      position: 'relative',
      borderTop: '1px solid rgba(212,170,82,0.06)',
      overflow: 'hidden',
    }}
  >
    {/* Soft drifting dust to extend the atmosphere all the way down */}
    {[0, 1].map((i) => (
      <div key={`final-dust-${i}`} aria-hidden style={{
        position: 'absolute', pointerEvents: 'none',
        width: 2 + (i % 2), height: 2 + (i % 2), borderRadius: '50%',
        background: 'rgba(255,230,180,0.28)', filter: 'blur(0.5px)',
        top: `${30 + i * 30}%`, left: '-4%',
        animation: `finalDust 24s linear ${i * 8}s infinite`,
      }} />
    ))}

    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
      style={{
        maxWidth: 720, margin: '0 auto', textAlign: 'center',
        position: 'relative', zIndex: 2,
      }}
    >
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
        color: GOLD_MUTED, marginBottom: 18,
      }}>
        Closing ✦
      </div>
      <h2 style={{
        margin: 0,
        fontFamily: SERIF,
        fontSize: 'clamp(32px, 4.8vw, 56px)',
        fontWeight: 500, lineHeight: 1.18,
        letterSpacing: '-0.015em',
        color: IVORY,
        textShadow: '0 0 24px rgba(212,170,82,0.18)',
      }}>
        Some nights fade.<br />
        The best ones keep breathing <span style={{ color: GOLD }}>✦</span>
      </h2>
      <p style={{
        margin: '28px auto 40px',
        maxWidth: 540,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(15px, 1.5vw, 18px)',
        color: 'rgba(255,235,200,0.66)', lineHeight: 1.65,
      }}>
        Begin yours, and EventDrop will quietly preserve the feeling — through the night, across the room, and long after the lights go out.
      </p>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 24, flexWrap: 'wrap',
      }}>
        <GhostPill to="/create" testId="final-cta-event">
          Begin your event <span style={{ color: GOLD, marginLeft: 4 }}>✦</span>
        </GhostPill>
        <QuietLink to="/classroom" testId="final-cta-film">
          Or wander through a real one →
        </QuietLink>
      </div>
    </motion.div>

    <style>{`
      @keyframes finalDust {
        0%   { transform: translate(0vw, 0) scale(0.8); opacity: 0; }
        15%  { opacity: 1; }
        85%  { opacity: 1; }
        100% { transform: translate(110vw, -40px) scale(1.1); opacity: 0; }
      }
    `}</style>
  </section>
);

// ────────────────────────────────────────────────────────────────
// 7 · FOOTER — quiet attribution
// ────────────────────────────────────────────────────────────────
export const EditorialFooter = () => (
  <footer
    data-testid="homepage-footer"
    style={{
      padding: '48px 28px',
      background: '#0A0604',
      borderTop: '1px solid rgba(212,170,82,0.06)',
      textAlign: 'center',
    }}
  >
    <div style={{
      fontFamily: SERIF,
      fontSize: 20, color: IVORY,
      letterSpacing: '0.01em', marginBottom: 12,
    }}>
      EventDrop <span style={{ color: GOLD, fontSize: 13, marginLeft: 2 }}>✦</span>
    </div>
    <p style={{
      margin: 0,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 12, color: 'rgba(255,235,200,0.42)',
      letterSpacing: '0.04em',
    }}>
      Made with care · for the nights worth keeping ·{' '}
      <a href="/privacy" style={{ color: GOLD_MUTED, textDecoration: 'underline', textDecorationColor: 'rgba(212,170,82,0.2)', textUnderlineOffset: 3 }}>privacy</a>
      {' · '}
      <a href="/terms" style={{ color: GOLD_MUTED, textDecoration: 'underline', textDecorationColor: 'rgba(212,170,82,0.2)', textUnderlineOffset: 3 }}>terms</a>
      {' · '}© {new Date().getFullYear()}
    </p>
  </footer>
);
